import React, { useEffect } from 'react';
import { Tab, Header, Icon } from 'semantic-ui-react';
import XTab from '../../common/tabs/XTab';
import { useDispatch } from 'react-redux';
import DefaultFinancialResourceParameters from './DefaultFinancialResourceParameters';
import alertsMetadata from '../../metadata/riskConfigurationsMetadata/riskAlertsThresholdsMetadata';
import stressEngineMetadata from '../../metadata/riskConfigurationsMetadata/stressEngineParamsMetadata';
import pricingMetadata from '../../metadata/riskConfigurationsMetadata/pricingEngineParamsMetadata';

import RiskConfigTable from './RiskConfigTable';
import sections from './riskConfigSections';
import InitialMarginParams from './InitialMarginParams';
import {
  BINARY_OPTION_DISCRETIONARY_ADD_ON_TEMPLATE,
  PRICING_ENGINE_PARAMS_TEMPLATE,
  RISK_ALERT_THRESHOLD_TEMPLATE,
  STRESS_ENGINE_TEMPLATE,
} from './newRowTemplates';
import { TABLE_EDIT_ACTIONS } from '../../common/table/constants';
import hasOneRow from './utils';
import { useCustomRiskConfigSave } from './useCustomRiskConfigSave';
import { transformBinaryOptionDiscretionaryAddOnBatch } from './riskConfigTransforms';
import { futuresProducts } from '../../reducers/futuresProductReducer';
import {
  fetchBinaryOptionDiscretionaryAddOns,
  updateBinaryOptionDiscretionaryAddOnPromise,
  getBinaryOptionDiscretionaryAddOnData,
  getBinaryOptionDiscretionaryAddOnCount,
  BINARY_OPTION_DISCRETIONARY_ADD_ONS,
} from '../../reducers/binaryOptionDiscretionaryAddOnReducer';
import FinancialTabs from './FinancialTabs';
import LargeTraderTabs from './LargeTraderTabs';
import binaryOptionDiscretionaryAddOnMetadata from 'metadata/riskConfigurationsMetadata/binaryOptionDiscretionaryAddOnMetadata';
import useEnableKpi from '../../hooks/useIsFeatureFlagEnabled/useEnableKpi';

const BINARY_OPTION_DISCRETIONARY_ADD_ON_URL = 'binary_option_discretionary_add_on';

const panes = (handleBinaryOptionSave) => [
  {
    url: 'post_trade_risk_alerts',
    menuItem: 'Post-Trade Risk Alerts',
    render: () => (
      <RiskConfigTable
        configSection={sections.RISK_ALERTS_THRESHOLDS}
        metadata={alertsMetadata}
        editable
        template={RISK_ALERT_THRESHOLD_TEMPLATE}
        key={sections.RISK_ALERTS_THRESHOLDS}
      />
    ),
  },
  {
    url: 'stress-engine-parameters',
    menuItem: 'Stress Engine Parameters',
    render: () => (
      <RiskConfigTable
        configSection={sections.STRESS_ENGINE_PARAMS}
        metadata={stressEngineMetadata}
        editable
        template={STRESS_ENGINE_TEMPLATE}
        key={sections.STRESS_ENGINE_PARAMS}
      />
    ),
  },
  {
    url: 'default_financial_resource_parameters',
    menuItem: 'Default Financial Resource Parameters',
    render: () => (
      <Tab.Pane attached={false}>
        <DefaultFinancialResourceParameters />
      </Tab.Pane>
    ),
  },
  {
    url: 'initial_margin_parameters',
    menuItem: 'Initial Margin Parameters',
    render: () => (
      <Tab.Pane attached={false}>
        <InitialMarginParams />
      </Tab.Pane>
    ),
  },
  {
    url: 'pricing_engine_parameters',
    menuItem: 'Pricing Engine Parameters',
    render: () => (
      <RiskConfigTable
        configSection={sections.PRICING_ENGINE_PARAMS}
        metadata={pricingMetadata}
        editable
        template={PRICING_ENGINE_PARAMS_TEMPLATE}
        hideActions={[TABLE_EDIT_ACTIONS.DELETE]}
        isAddNewDisabled={hasOneRow}
        key={sections.PRICING_ENGINE_PARAMS}
      />
    ),
  },
  {
    url: 'large-trader',
    menuItem: 'Large Trader',
    render: () => (
      <Tab.Pane attached={false}>
        <LargeTraderTabs />
      </Tab.Pane>
    ),
  },
  {
    url: 'financial',
    menuItem: 'Financial',
    render: () => (
      <Tab.Pane attached={false}>
        <FinancialTabs />
      </Tab.Pane>
    ),
  },
  {
    url: BINARY_OPTION_DISCRETIONARY_ADD_ON_URL,
    menuItem: 'Binary Option Discretionary Add-on',
    render: () => (
      <RiskConfigTable
        configSection={sections.BINARY_OPTION_DISCRETIONARY_ADD_ON}
        metadata={binaryOptionDiscretionaryAddOnMetadata}
        editable
        hideActions={[TABLE_EDIT_ACTIONS.CREATE, TABLE_EDIT_ACTIONS.DELETE]}
        fetchAction={fetchBinaryOptionDiscretionaryAddOns}
        fetchParams={{}}
        selectorData={getBinaryOptionDiscretionaryAddOnData}
        selectorCount={getBinaryOptionDiscretionaryAddOnCount}
        loadingKey={BINARY_OPTION_DISCRETIONARY_ADD_ONS}
        onSaveOverride={handleBinaryOptionSave}
        key={sections.BINARY_OPTION_DISCRETIONARY_ADD_ON}
      />
    ),
  },
];

const RiskConfigurations = () => {
  const dispatch = useDispatch();
  const enableKpi = useEnableKpi();
  useEffect(() => dispatch(futuresProducts()), []);

  const handleBinaryOptionSave = useCustomRiskConfigSave(
    updateBinaryOptionDiscretionaryAddOnPromise,
    fetchBinaryOptionDiscretionaryAddOns,
    transformBinaryOptionDiscretionaryAddOnBatch,
  );

  const filteredPanes = panes(handleBinaryOptionSave).filter((pane) => {
    // Filter Binary Option Discretionary Add-on tab based on ENABLE_KPI flag
    if (pane.url === BINARY_OPTION_DISCRETIONARY_ADD_ON_URL && !enableKpi) {
      return false;
    }
    return true;
  });

  return (
    <>
      <Header as="h1" dividing>
        Risk Parameters Configuration
      </Header>
      <XTab panes={filteredPanes} />
    </>
  );
};

export default RiskConfigurations;
